/**
 * A bundle of levers, and the two operations on one.
 *
 * A bundle is total over the fields rather than partial, so `combineEffects` never has to ask
 * whether a field is present and no consumer ever reads `undefined` and treats it as zero. The
 * identity is one across the board, which is what makes `EMPTY_EFFECTS` the identity of the sum and
 * what lets a character with nothing equipped and no perks taken run the same code path as one
 * with everything.
 *
 * Combination is multiplicative, matching how affixes meet a weapon spec. Two sources that each
 * take a tenth off a reload leave eighty one percent, not eighty, and that is the intended shape:
 * a lever approaches its bound rather than crossing it.
 */

import { EFFECT_FIELD_ORDER, effectField } from '@/sim/effects/effectFieldContract';
import type { EffectFieldId } from '@/sim/effects/effectFieldContract';

export type EffectBundle = Readonly<Record<EffectFieldId, number>>;

/**
 * A character pulling no levers. Written out rather than built from the order, so a field added to
 * the union and forgotten here is a type error rather than a bundle with a hole in it.
 */
export const EMPTY_EFFECTS: EffectBundle = {
  reloadSeconds: 1,
  adsSeconds: 1,
  weaponSpread: 1,
  criticalChance: 1,
  incomingDamage: 1,
  bleedoutSeconds: 1,
  reviveSeconds: 1,
  moveSpeed: 1,
  interactSeconds: 1,
  lootQuality: 1,
  carryKg: 1,
};

/** Pulls a lever's multiplier out of a bundle, clamped to the field's bounds. */
export function effectScale(effects: EffectBundle, id: EffectFieldId): number {
  return clampField(id, effects[id]);
}

/**
 * Two bundles as one. Walked in `EFFECT_FIELD_ORDER` rather than by object key, so two peers
 * holding the same build multiply in the same sequence and land on the same last bit.
 */
export function combineEffects(a: EffectBundle, b: EffectBundle): EffectBundle {
  const out: Record<EffectFieldId, number> = { ...EMPTY_EFFECTS };

  for (const id of EFFECT_FIELD_ORDER) out[id] = clampField(id, a[id] * b[id]);

  return out;
}

/**
 * One lever moved by a fraction, as a bundle. `-0.1` on `reloadSeconds` is a tenth off it. The one
 * place a perk's authored number becomes a multiplier, so the sign convention is decided once.
 */
export function effectFrom(id: EffectFieldId, fraction: number): EffectBundle {
  const out: Record<EffectFieldId, number> = { ...EMPTY_EFFECTS };

  out[id] = clampField(id, 1 + (Number.isFinite(fraction) ? fraction : 0));

  return out;
}

function clampField(id: EffectFieldId, value: number): number {
  const field = effectField(id);

  if (!Number.isFinite(value)) return 1;
  if (value < field.min) return field.min;
  if (value > field.max) return field.max;
  return value;
}
